// Public (pre-login) knowledge — the ONLY things the sign-in-page assistant
// knows offline. Deliberately non-PHI: no claim/member/eligibility lookups.
// Mirrors the backend PUBLIC_KB (publicChat.js) so online and offline agree.

// Electronic claims (837) payer routing. TODO(Central Health): fill in the
// official payer ID and preferred clearinghouse — while blank, the answer tells
// providers to confirm with Provider Services instead of guessing.
export const PAYER_INFO = { payerId: '', clearinghouse: '' };
const PAYER_INFO_TEXT = PAYER_INFO.payerId
  ? `Submit electronic claims (837P/837I) through ${PAYER_INFO.clearinghouse || 'your clearinghouse'} using Central Health payer ID ${PAYER_INFO.payerId}. Some clearinghouses use their own ID for the same payer, so confirm the mapping with yours. Enroll in ERA/EFT after you sign in so remittances and payments come back electronically.`
  : 'Central Health’s electronic payer ID and clearinghouse details aren’t listed in the portal yet — please confirm them with Provider Services at 512-978-8669 (toll-free 844-316-2004) or chproviderservices@centralhealth.net, then submit 837P/837I claims through your clearinghouse using that payer ID. Paper claims go to Central Health, P.O. Box 17337, Austin, TX 78760.';

export const PUBLIC_TOPICS = [
  {
    key: 'eft',
    chip: 'Sign up for EFT',
    q: ['eft', 'direct deposit', 'electronic funds', 'get paid electronically'],
    title: 'Sign up for EFT (direct deposit)',
    answer: 'Here’s how to enroll in EFT (direct deposit):',
    steps: [
      'Sign in, then open Forms & Resources → EFT Enrollment.',
      'Have your NPI, Tax ID (TIN), and bank routing + account numbers ready.',
      'Pick a reason: New enrollment, Change, or Cancel.',
      'Enter your provider and banking details and link the account to your NPI or TIN.',
      'An authorized signer e-signs and submits — you get a confirmation number, and deposits begin on your effective date.',
    ],
  },
  {
    key: 'era',
    chip: 'Sign up for ERA',
    q: ['era', '835', 'remittance advice', 'electronic remittance'],
    title: 'Sign up for ERA (835 remittance)',
    answer: 'Here’s how to enroll in ERA (the 835 electronic remittance):',
    steps: [
      'Sign in, then open Forms & Resources → ERA Enrollment.',
      'Have your NPI, TIN, and clearinghouse/vendor name ready.',
      'Pick a reason: New enrollment, Change, or Cancel.',
      'Set your remittance preferences — how the 835 is aggregated (by TIN or NPI) and how you retrieve it.',
      'An authorized signer e-signs and submits. Enroll in both EFT and ERA so payments and remittances auto-reassociate.',
    ],
  },
  {
    key: 'password',
    chip: 'Reset my password',
    q: ['password', 'reset password', 'forgot password', 'locked out', 'change password', 'cant log in', "can't log in", 'cant sign in'],
    title: 'Reset your password',
    answer: 'Here’s how to reset your password:',
    steps: [
      'On this sign-in screen, click "Forgot Password?".',
      'Enter the email address on your account and click "Send Reset Link".',
      'Check your email (including spam) for the reset link and open it.',
      'Choose a new password — at least 8 characters with an uppercase letter, a lowercase letter, and a number — and confirm it.',
      'Sign in with the new password. Already signed in? You can also change it under Profile → Security.',
    ],
    cta: { label: 'Forgot Password', route: '/forgot-password' },
  },
  {
    key: 'username',
    chip: 'Recover my username',
    q: ['username', 'user name', 'forgot username', 'recover username', 'user id', 'forgot my login', 'what is my username'],
    title: 'Recover your username',
    answer: 'Here’s how to recover your username:',
    steps: [
      'On this sign-in screen, click "Forgot Username?".',
      'Enter the email address on file for your account.',
      'Click "Send My Username".',
      'Check your email (including spam) — we’ll send your username there.',
      'Go back to the sign-in screen and log in. If nothing arrives, contact your portal administrator.',
    ],
    cta: { label: 'Forgot Username', route: '/forgot-username' },
  },
  {
    key: 'register',
    chip: 'Create an account',
    q: ['register', 'create account', 'create an account', 'new account', 'get access', 'how do i join', 'how to join', 'sign up for the portal', 'register my practice'],
    title: 'Create a provider account',
    answer: 'Here’s how to create a provider account:',
    steps: [
      'On this sign-in screen, click "Create Account".',
      'Provider Info: enter your first name, last name, title (e.g. MD, RN, NP), and email.',
      'Organization Info: enter your Tax ID (TIN), organization name, address, city, state, ZIP, and phone.',
      'Password Info: choose a username and password (at least 8 characters with an uppercase, a lowercase, and a number), then accept the Terms of Service and Privacy Policy.',
      'Submit — we’ll email a verification link. Verify your email, then an administrator reviews and approves your access before you can sign in.',
    ],
    cta: { label: 'Create Account', route: '/register' },
  },
  {
    key: 'paper_claim',
    chip: 'File a paper claim',
    q: ['paper claim', 'mail a claim', 'cms-1500', 'cms 1500', 'ub-04', 'ub04', 'file a claim by mail', 'where do i send claims', 'where to send claims', 'claims address', 'mailing address', 'po box'],
    title: 'File a paper claim',
    answer: 'Here’s how to file a claim (electronic is fastest):',
    steps: [
      'Prefer electronic: submit 837P/837I through your clearinghouse for the fastest turnaround.',
      'For paper, complete a current CMS-1500 (professional) or UB-04 (facility) claim form.',
      'Mail it to: Central Health, P.O. Box 17337, Austin, TX 78760 (the same address handles written appeals).',
      'Include the member ID on every page and attach any supporting documentation.',
      'After you sign in, download forms under Forms & Resources and track claims under Claims & Payments.',
    ],
  },
  {
    key: 'payer_id',
    chip: 'Clearinghouse & payer ID',
    q: ['payer id', 'payor id', 'clearinghouse', 'clearing house', 'edi id', '837', 'electronic claims', 'submit claims electronically'],
    title: 'Clearinghouse & payer ID',
    answer:
      // TODO(Central Health): fill in the official payer ID / clearinghouse (see PAYER_INFO below).
      PAYER_INFO_TEXT,
  },
  {
    key: 'prior_auth',
    chip: 'Prior auth / referral',
    q: ['prior auth', 'prior authorization', 'pre-auth', 'preauth', 'authorization', 'referral', 'how do i get an auth', 'auth form', 'utilization'],
    title: 'Submit a prior authorization or referral',
    answer: 'Here’s how to submit a prior authorization / referral request:',
    steps: [
      'Sign in and download the Central Health Prior-Authorization Form from Forms & Resources.',
      'Mark Routine or Urgent (service within 72 hours) and complete every * field — patient name/DOB/ID, requesting and requested provider NPIs, ICD-10 and CPT/HCPCS codes, visits, and proposed date of service.',
      'Attach clinical/progress notes or a clinical narrative supporting the request.',
      'Fax it to (512) 776-0485 or email priorauthrequests@centralhealth.net. Central Health returns the authorization number, dates, and approved visits to your return fax.',
    ],
  },
  {
    key: 'claim_status',
    chip: 'Check a claim status',
    q: ['claim status', 'check a claim', 'track a claim', 'where is my claim', 'claim paid'],
    title: 'Check a claim status',
    answer: 'Here’s how to check a claim status (after you sign in):',
    steps: [
      'Sign in, then open Claims & Payments → Claim Status from the left sidebar.',
      'Choose your billing provider and search by claim number, member ID, or a service date range.',
      'Open the claim to see its status, EOP, payment detail, and to download the 835 (ERA) or file an appeal.',
    ],
  },
  {
    key: 'appeal',
    chip: 'Appeal a claim',
    q: ['appeal', 'reconsideration', 'dispute a claim', 'denied claim', 'my appeals'],
    title: 'Appeal / reconsider a claim',
    answer: 'Here’s how to appeal a claim decision (after you sign in):',
    steps: [
      'Sign in and open the claim under Claims & Payments → Claim Status, noting the denial reason (CARC/RARC) on the EOP.',
      'Click “File Appeal”, pick a reason, add a justification, and attach supporting documents, then submit.',
      'Track it under Claims & Payments → My Appeals. Written appeals can also be mailed to Central Health, P.O. Box 17337, Austin, TX 78760.',
    ],
  },
  {
    key: 'eop',
    chip: 'Find an EOP',
    q: ['eop', 'explanation of payment', 'remittance', 'check number', 'find an eop'],
    title: 'Find an EOP / remittance',
    answer: 'Here’s how to find an EOP (after you sign in):',
    steps: [
      'Sign in and open EOP Search from the left sidebar.',
      'Search by billing provider, check number, or service dates.',
      'Open the matching EOP to download the PDF/CSV, or download the 835 (ERA) for that check.',
    ],
  },
  {
    key: 'eligibility',
    chip: 'Verify eligibility',
    q: ['eligibility', 'eligible', 'coverage', 'benefits', 'is the member covered'],
    title: 'Verify member eligibility',
    answer: 'Here’s how to verify eligibility (after you sign in):',
    steps: [
      'Sign in and open Eligibility & Benefits from the left sidebar.',
      'Search by member ID, or by name and date of birth.',
      'Review coverage dates, plan, copays, deductible, and PCP on the member record.',
    ],
  },
  {
    key: 'forms',
    chip: 'Download a form',
    q: ['form', 'forms', 'download a form', 'prior auth form', 'w-9', 'provider manual'],
    title: 'Download a provider form',
    answer: 'Here’s how to download forms (after you sign in):',
    steps: [
      'Sign in and open Forms & Resources → Forms & Downloads.',
      'Pick the form (EFT, prior authorization, provider information change, appeal request) and download it.',
      'Complete it and submit per the instructions on the form.',
    ],
  },
  {
    key: 'add_user',
    chip: 'Add a user',
    q: ['add a user', 'add user', 'new user', 'invite staff', 'user management', 'create a login for'],
    title: 'Add a portal user',
    answer: 'Here’s how a Provider Admin adds a user (after you sign in):',
    steps: [
      'Sign in as a Provider Admin and open User Management from the left sidebar.',
      'Click “Add User”, enter their name, email, phone, and username, choose a role, and set an initial password.',
      'Save and share the credentials securely — the user should change the password at first sign-in.',
    ],
  },
  {
    key: 'tax_id',
    chip: 'Add a Tax ID',
    q: ['tax id', 'tin', 'ein', 'additional tax id', 'another tax id', 'add tax id'],
    title: 'Add an additional Tax ID',
    answer: 'Here’s how to add a Tax ID (after you sign in):',
    steps: [
      'Sign in as a Provider Admin and open Profile from the left sidebar.',
      'In the Tax IDs section click “Add another Tax ID”, enter the 9-digit TIN/EIN, and check the attestation box.',
      'Click Save — claims and EOPs for the new Tax ID appear once it is active on your account.',
    ],
  },
  {
    key: 'profile',
    chip: 'Update my profile',
    q: ['profile', 'update my profile', 'my details', 'change my email', 'change my phone', 'mfa', 'two-factor'],
    title: 'Update your profile',
    answer: 'Here’s how to update your profile (after you sign in):',
    steps: [
      'Sign in and open Profile from the left sidebar.',
      'Edit your name, contact details, Tax IDs, and notification preferences.',
      'Use the Security section to change your password or manage multi-factor authentication, then save.',
    ],
  },
  {
    key: 'what',
    chip: 'What can the portal do?',
    q: ['what can', 'what is this', 'what does the portal', 'features', 'help with', 'what can you do'],
    title: 'About the provider portal',
    answer:
      'The portal lets your practice verify eligibility, check claim status and payments, file claim appeals, ' +
      'download EOPs/835 remittances, enroll in EFT/ERA, and manage users. Sign in to use those — ' +
      'here I can only help with general getting-started questions.',
  },
  {
    key: 'contact',
    chip: 'Contact support',
    q: ['contact', 'phone', 'call', 'support', 'help desk', 'who do i contact', 'reach you'],
    title: 'Contact & support',
    answer:
      'Provider Services: 512-978-8669 (toll-free 844-316-2004) · chproviderservices@centralhealth.net. Use the Contact ' +
      'link on this page for department details. Clinical or claim-specific questions require a verified account — please sign in first.',
    cta: { label: 'Contact', route: '/contact' },
  },
];

export const PUBLIC_CHIPS = PUBLIC_TOPICS.map((t) => t.chip);
const CHIP_TO_TOPIC = Object.fromEntries(PUBLIC_TOPICS.map((t) => [t.chip, t]));

// Text that looks like a PHI lookup → refuse (there is nothing to look up here).
const PHI_PATTERNS = [
  /\b(snd|mbr|mem)-?\d{3,}\b/i, /\bclm-\d{3,}\b/i, /\bauth-\d{3,}\b/i, /\beop-\d{3,}\b/i,
  /\b\d{3}-\d{2}-\d{4}\b/, /\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/,
  /\bmember\s*id\b|\bdate of birth\b|\bdob\b|\bssn\b|social security/i,
];
export const looksLikePhi = (t = '') => PHI_PATTERNS.some((re) => re.test(t));

const PHI_REFUSAL =
  "I can't look up member, claim, eligibility, or payment details here — that needs a verified sign-in to protect " +
  'patient privacy. Please sign in, then use the assistant inside. I can still help with general questions like ' +
  'EFT/ERA enrollment, resetting your password, or creating an account.';

/** Offline public responder → { text, cta?, chips? }. Never touches PHI. */
export function runPublicQuery(raw) {
  const text = String(raw || '').trim();
  // Chip tap → direct topic.
  if (CHIP_TO_TOPIC[text]) {
    const t = CHIP_TO_TOPIC[text];
    return { text: t.answer, cta: t.cta, steps: t.steps, stepsTitle: t.title };
  }
  if (looksLikePhi(text)) return { text: PHI_REFUSAL };

  const s = text.toLowerCase();
  if (/\b(hi|hello|hey|help|what can you do)\b/.test(s) && s.length < 40) {
    return { text: 'Hi! I can help you get into the portal. Pick a topic:', chips: PUBLIC_CHIPS };
  }
  let best = null, score = 0;
  for (const t of PUBLIC_TOPICS) {
    const sc = t.q.reduce((n, kw) => (s.includes(kw) ? n + kw.length : n), 0);
    if (sc > score) { score = sc; best = t; }
  }
  if (best) return { text: best.answer, cta: best.cta, steps: best.steps, stepsTitle: best.title };
  return {
    text: "I can help with general portal questions — creating an account, resetting your password or username, and " +
      'enrolling in EFT/ERA. For anything about specific members, claims, or payments, please sign in first.',
    chips: PUBLIC_CHIPS,
  };
}
